/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type {Klass, LexicalNode, LexicalNodeConfig} from 'lexical';

import {CodeHighlightNode, CodeNode} from '@lexical/code';
import {HashtagNode} from '@lexical/hashtag';
import {AutoLinkNode, LinkNode} from '@lexical/link';
import {ListItemNode, ListNode} from '@lexical/list';
import {MarkNode} from '@lexical/mark';
import {OverflowNode} from '@lexical/overflow';
import {HorizontalRuleNode} from '@lexical/extension';
import {HeadingNode} from '@lexical/rich-text';
import {TableCellNode, TableNode, TableRowNode} from '@lexical/table';

import { ContainerQuoteNode } from '@/components/editor/lexicalPlugins/quote/ContainerQuoteNode';

import { CollapsibleSectionContainerNode } from '@/components/editor/lexicalPlugins/collapsibleSections/CollapsibleSectionContainerNode';
import { CollapsibleSectionContentNode } from '@/components/editor/lexicalPlugins/collapsibleSections/CollapsibleSectionContentNode';
import { CollapsibleSectionTitleNode } from '@/components/editor/lexicalPlugins/collapsibleSections/CollapsibleSectionTitleNode';
import {AutocompleteNode} from './AutocompleteNode';
import {DateTimeNode} from './DateTimeNode/DateTimeNode';
import {EmojiNode} from './EmojiNode';
import { MathNode } from '@/components/editor/lexicalPlugins/math/MathNode';
// import {ExcalidrawNode} from './ExcalidrawNode';
import {FigmaNode} from './FigmaNode';
import {ImageCaptionNode, ImageNode, ImageRenderNode} from './ImageNode';
import {KeywordNode} from './KeywordNode';
import {LayoutContainerNode} from './LayoutContainerNode';
import {LayoutItemNode} from './LayoutItemNode';
import {MentionNode} from './MentionNode';
import {PageBreakNode} from './PageBreakNode';
import {PollNode} from './PollNode';
import {SpecialTextNode} from './SpecialTextNode';
// import {StickyNode} from './StickyNode';
// import {TweetNode} from '../embeds/TwitterEmbed/TweetNode';
import {YouTubeNode} from '../embeds/YouTubeEmbed/YouTubeNode';
import {MetaculusNode} from '../embeds/MetaculusEmbed/MetaculusNode';
import {ThoughtsaverNode} from '../embeds/ThoughtsaverEmbed/ThoughtsaverNode';
import {ManifoldNode} from '../embeds/ManifoldEmbed/ManifoldNode';
import {NeuronpediaNode} from '../embeds/NeuronpediaEmbed/NeuronpediaNode';
import {StrawpollNode} from '../embeds/StrawpollEmbed/StrawpollNode';
import {MetaforecastNode} from '../embeds/MetaforecastEmbed/MetaforecastNode';
import {OWIDNode} from '../embeds/OWIDEmbed/OWIDNode';
import {EstimakerNode} from '../embeds/EstimakerEmbed/EstimakerNode';
import {ViewpointsNode} from '../embeds/ViewpointsEmbed/ViewpointsNode';
import {CalendlyNode} from '../embeds/CalendlyEmbed/CalendlyNode';
import {LWArtifactsNode} from '../embeds/LWArtifactsEmbed/LWArtifactsNode';
import { SpoilerNode } from '@/components/editor/lexicalPlugins/spoilers/SpoilerNode';
import { ClaimNode } from '../embeds/ElicitEmbed/ClaimNode';
import { FootnoteBackLinkNode } from '@/components/editor/lexicalPlugins/footnotes/FootnoteBackLinkNode';
import { FootnoteContentNode } from '@/components/editor/lexicalPlugins/footnotes/FootnoteContentNode';
import { FootnoteItemNode } from '@/components/editor/lexicalPlugins/footnotes/FootnoteItemNode';
import { FootnoteReferenceNode } from '@/components/editor/lexicalPlugins/footnotes/FootnoteReferenceNode';
import { FootnoteSectionNode } from '@/components/editor/lexicalPlugins/footnotes/FootnoteSectionNode';
import { ProtonNode } from '@/components/editor/lexicalPlugins/suggestedEdits/ProtonNode';
import { ReviewResultsTableNode } from '../embeds/ReviewResultsEmbed/ReviewResultsTableNode';
import { IframeWidgetNode } from '../embeds/IframeWidgetEmbed/IframeWidgetNode';
import { SentinelParagraphNode } from '@/components/editor/lexicalPlugins/blockCursorNavigation/SentinelParagraphNode';
import { LLMContentBlockNode } from '@/components/editor/lexicalPlugins/llmContentOutput/LLMContentBlockNode';
import { LLMContentBlockHeaderNode } from '@/components/editor/lexicalPlugins/llmContentOutput/LLMContentBlockHeaderNode';
import { LLMContentBlockContentNode } from '@/components/editor/lexicalPlugins/llmContentOutput/LLMContentBlockContentNode';

function validateLexicalNodes(nodes: Record<string, LexicalNodeConfig>): LexicalNodeConfig[] {
  for (const nodeName of Object.keys(nodes)) {
    if (!nodes[nodeName]) {
      throw new Error(`Node ${nodeName} is nullish`);
    }
  }
  return Object.values(nodes);
}

const PlaygroundNodes: Array<LexicalNodeConfig> = validateLexicalNodes({
  HeadingNode,
  ListNode,
  ListItemNode,
  // ContainerQuoteNode replaces the built-in QuoteNode. It acts as a shadow
  // root so block-level content (lists, collapsible sections, etc.) can be
  // nested inside blockquotes. Registered directly (same type 'quote').
  ContainerQuoteNode,
  CodeNode,
  TableNode,
  TableCellNode,
  TableRowNode,
  HashtagNode,
  CodeHighlightNode,
  AutoLinkNode,
  LinkNode,
  OverflowNode,
  PollNode,
  // StickyNode,
  ImageNode,
  ImageRenderNode,
  ImageCaptionNode,
  MentionNode,
  EmojiNode,
  // ExcalidrawNode,
  MathNode,
  AutocompleteNode,
  KeywordNode,
  HorizontalRuleNode,
  // TweetNode,
  YouTubeNode,
  MetaculusNode,
  ThoughtsaverNode,
  ManifoldNode,
  NeuronpediaNode,
  StrawpollNode,
  MetaforecastNode,
  OWIDNode,
  EstimakerNode,
  ViewpointsNode,
  CalendlyNode,
  LWArtifactsNode,
  FigmaNode,
  MarkNode,
  CollapsibleSectionContainerNode,
  CollapsibleSectionContentNode,
  CollapsibleSectionTitleNode,
  PageBreakNode,
  LayoutContainerNode,
  LayoutItemNode,
  SpecialTextNode,
  DateTimeNode,

  // Custom plugin nodes
  FootnoteReferenceNode,
  FootnoteSectionNode,
  FootnoteItemNode,
  FootnoteContentNode,
  FootnoteBackLinkNode,
  SpoilerNode,
  ClaimNode,
  ProtonNode,
  SentinelParagraphNode,
  ReviewResultsTableNode,
  IframeWidgetNode,
  LLMContentBlockNode,
  LLMContentBlockHeaderNode,
  LLMContentBlockContentNode,
});

export default PlaygroundNodes;
